import Dropcursor from '@tiptap/extension-dropcursor';
import type { DropcursorOptions } from '@tiptap/extension-dropcursor';
import { EditorContent, useEditor } from '@tiptap/react';
import React from 'react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';
import { SharedMention, type MentionItem } from './shared-mention';
import { ReactRenderer } from '@tiptap/react';
import { MentionList } from './mention-list';
import StarterKit from '@tiptap/starter-kit';
import { EditorHeader, type EditorHeaderAction } from '../shared/editor-header';
import { FormattingToolbar } from '../shared/formatting-toolbar';

export interface StepsEditorRef {
  getHTML: () => string;
  setContent: (content: string) => void;
  getSteps: () => string[];
}

export interface StepsEditorProps {
  ingredientNames?: string[];
  appliances?: readonly string[];
  editable?: boolean;
  debug?: boolean;
  initialContent?: string;
  onChange?: () => void;
}

export const StepsEditor = React.forwardRef<StepsEditorRef, StepsEditorProps>(
  (
    {
      ingredientNames = [],
      appliances = [],
      editable = true,
      debug = false,
      initialContent,
      onChange,
    },
    ref,
  ) => {
    // Use ref to store current ingredient names to avoid editor recreation
    const ingredientNamesRef = React.useRef(ingredientNames);

    // Update the ref whenever ingredientNames changes
    React.useEffect(() => {
      ingredientNamesRef.current = ingredientNames;
    }, [ingredientNames]);

    const getAllMentionItems = React.useCallback(
      (query: string): MentionItem[] => {
        const lowerQuery = (query || '').toLowerCase();

        // Get all appliances, filtered by query if provided
        const applianceItems: MentionItem[] = appliances
          .filter((appliance): appliance is string => {
            // If no query, show all appliances
            if (!lowerQuery) return true;
            return appliance.toLowerCase().includes(lowerQuery);
          })
          .map((appliance) => ({
            id: appliance,
            label: appliance,
            type: 'appliance' as const,
          }));

        // Get all ingredients, filtered by query if provided
        const ingredientItems: MentionItem[] = ingredientNamesRef.current
          .filter((ingredient): ingredient is string => {
            if (!ingredient) return false;
            // If no query, show all ingredients
            if (!lowerQuery) return true;
            return ingredient.toLowerCase().includes(lowerQuery);
          })
          .map((ingredient) => ({
            id: ingredient,
            label: ingredient,
            type: 'ingredient' as const,
          }));

        // Combine and sort: appliances first, then ingredients
        const allItems = [...applianceItems, ...ingredientItems];
        return allItems;
      },
      [appliances], // Include appliances in dependencies
    );

    // Memoize extensions with stable dependencies to prevent recreation
    const extensions = React.useMemo(
      () => [
        StarterKit.configure({
          dropcursor: false, // Disable built-in dropcursor to avoid conflicts
          heading: {
            levels: [1, 2, 3],
          },
          blockquote: {},
          // Enable both ordered and unordered lists
          orderedList: {},
          bulletList: {},
          listItem: {},
        }),
        // Drop indicator bar
        Dropcursor.configure({
          color: '#0a84ff',
          width: 3,
        } satisfies Partial<DropcursorOptions>),
        SharedMention.configure({
          suggestion: {
            char: '@',
            allowSpaces: false,
            allowedPrefixes: [' ', '\n'],
            startOfLine: false,
            items: ({ query }: { query: string }): MentionItem[] => {
              return getAllMentionItems(query || '');
            },
            render: () => {
              let component: ReactRenderer;

              return {
                onStart: (props: {
                  items: MentionItem[];
                  editor: Editor;
                  clientRect: () => DOMRect | null;
                }) => {
                  component = new ReactRenderer(MentionList, {
                    props: {
                      ...props,
                      items: props.items,
                    },
                    editor: props.editor,
                  });

                  if (!props.clientRect) {
                    return;
                  }

                  // Simple positioning - append to body
                  document.body.appendChild(component.element);
                },
                onUpdate: (props: {
                  items: MentionItem[];
                  editor: Editor;
                  clientRect: () => DOMRect | null;
                }) => {
                  component?.updateProps({
                    ...props,
                    items: props.items,
                  });

                  if (!props.clientRect) {
                    return;
                  }

                  // Update position
                  const rect = props.clientRect();
                  if (rect && component.element instanceof HTMLElement) {
                    Object.assign(component.element.style, {
                      position: 'fixed',
                      top: `${rect.bottom + 8}px`,
                      left: `${rect.left}px`,
                      zIndex: '1000',
                    });
                  }
                },
                onKeyDown: (props: { event: KeyboardEvent }) => {
                  if (props.event.key === 'Escape') {
                    return true;
                  }
                  const api = component.ref as
                    | { onKeyDown?: (p: { event: KeyboardEvent }) => boolean }
                    | null
                    | undefined;
                  return api?.onKeyDown?.(props) ?? false;
                },
                onExit: () => {
                  if (component?.element?.parentNode) {
                    component.element.parentNode.removeChild(component.element);
                  }
                  component?.destroy();
                },
              };
            },
            command: ({
              editor,
              range,
              props,
            }: {
              editor: Editor;
              range: { from: number; to: number };
              props: { id: string };
            }) => {
              const mentionItems = getAllMentionItems('');

              // Normalize the search term
              const normalizedPropsId = props.id?.trim().toLowerCase() || '';
              const selectedItem = mentionItems.find((item) => {
                const normalizedLabel = item.label.trim().toLowerCase();
                const normalizedId = item.id.trim().toLowerCase();
                return (
                  normalizedLabel === normalizedPropsId ||
                  normalizedId === normalizedPropsId
                );
              });

              if (selectedItem) {
                editor
                  .chain()
                  .focus()
                  .insertContentAt(range, [
                    {
                      type: 'mention',
                      attrs: {
                        id: selectedItem.id,
                        label: selectedItem.label,
                        type: selectedItem.type,
                      },
                    },
                    {
                      type: 'text',
                      text: ' ',
                    },
                  ])
                  .run();
              }
            },
          },
        }),
      ],
      [], // Remove dependency to prevent recreation
    );

    const editor = useEditor(
      {
        editable,
        extensions,
        // If initialContent is explicitly provided (even empty string),
        // use it as-is. Only fall back to default placeholder when
        // initialContent is undefined.
        content:
          initialContent !== undefined
            ? initialContent
            : '<ol><li>Start typing your first step...</li></ol>',
        onUpdate: () => {
          onChange?.();
        },
      },
      [editable], // Only depend on editability to prevent unnecessary recreations
    );

    const getSteps = React.useCallback((): string[] => {
      if (!editor) return [];

      const steps: string[] = [];
      const doc = editor.state.doc;

      doc.descendants((node: ProseMirrorNode) => {
        if (node.type.name === 'listItem') {
          const stepText = node.textContent.trim();
          if (stepText) {
            steps.push(stepText);
          }
        }
      });

      return steps;
    }, [editor]);

    // Handle copying the entire HTML content
    const handleCopyHTML = React.useCallback(() => {
      if (!editor) return;

      const html = editor.getHTML();

      // Copy to clipboard
      if (window.navigator.clipboard) {
        window.navigator.clipboard.writeText(html).catch(console.error);
      }
    }, [editor]);

    // Handle clearing all steps
    const handleClearAll = React.useCallback(() => {
      if (!editor) return;
      editor.commands.setContent(
        '<ol><li>Start typing your first step...</li></ol>',
      );
      onChange?.();
    }, [editor, onChange]);

    React.useImperativeHandle(
      ref,
      (): StepsEditorRef => ({
        getHTML: () => editor?.getHTML() || '',
        setContent: (content: string) => {
          editor?.commands.setContent(content);
        },
        getSteps,
      }),
      [editor, getSteps],
    );

    // Prepare header actions
    const headerActions: EditorHeaderAction[] = [
      { label: 'Clear All', onClick: handleClearAll },
      { label: 'Copy HTML', onClick: handleCopyHTML },
    ];

    return (
      <div className="w-full h-full flex flex-col text-gray-900 dark:text-white">
        <EditorHeader
          title="Steps"
          actions={headerActions}
          debug={debug}
          editable={editable}
          statusBadgeColor="blue"
        />

        {/* Formatting Toolbar */}
        {editable && <FormattingToolbar editor={editor} />}

        {/* Steps Content - Full Width */}
        <div className="flex-1 overflow-auto">
          <div className="steps-editor h-full">
            <EditorContent editor={editor} spellCheck={false} />
          </div>
        </div>
      </div>
    );
  },
);

StepsEditor.displayName = 'StepsEditor';
